import { persistStaffAction } from "./staffActions";

export const AUTH_KEYS = [
  "email",
  "_id",
  "token",
  "loginTime",
  "token_expire_time",
  "permissionArray",
  "Role",
  "name",
  "authSessionId",
  "hospital_id",
  "referral_id",
  "staffAction",
];

export const SESSION_KEYS = ["tabenquiry", "patientMainTab"];

const MANAGED_KEYS = [...AUTH_KEYS, ...SESSION_KEYS];

export const getAppPrefix = () => {
  if (process.env.REACT_APP_AUTH_PREFIX) {
    const prefix = process.env.REACT_APP_AUTH_PREFIX;
    return prefix.endsWith("_") ? prefix : `${prefix}_`;
  }

  if (typeof window === "undefined") {
    return "crm_";
  }

  const path = window.location.pathname.toLowerCase();
  if (path === "/hospital" || path.startsWith("/hospital/")) {
    return "hospital_";
  }

  return "crm_";
};

export const authKey = (key) => `${getAppPrefix()}${key}`;

export const getKnownUserIdKey = () => `${getAppPrefix()}knownUserId`;

export const isManagedStorageKey = (storageKey) => {
  const prefix = getAppPrefix();
  if (storageKey.startsWith(prefix)) {
    return MANAGED_KEYS.includes(storageKey.slice(prefix.length));
  }
  return false;
};

let patchInstalled = false;

export function installAuthStoragePatch() {
  if (patchInstalled || typeof window === "undefined") {
    return;
  }

  patchInstalled = true;

  const originalGetItem = Storage.prototype.getItem;
  const originalSetItem = Storage.prototype.setItem;
  const originalRemoveItem = Storage.prototype.removeItem;

  Storage.prototype.getItem = function (key) {
    if (this !== localStorage || !MANAGED_KEYS.includes(key)) {
      return originalGetItem.call(this, key);
    }

    const prefixedValue = originalGetItem.call(this, authKey(key));
    if (prefixedValue !== null) {
      return prefixedValue;
    }

    if (getAppPrefix() === "crm_") {
      return originalGetItem.call(this, key);
    }

    return null;
  };

  Storage.prototype.setItem = function (key, value) {
    if (this !== localStorage || !MANAGED_KEYS.includes(key)) {
      return originalSetItem.call(this, key, value);
    }

    return originalSetItem.call(this, authKey(key), value);
  };

  Storage.prototype.removeItem = function (key) {
    if (this !== localStorage || !MANAGED_KEYS.includes(key)) {
      return originalRemoveItem.call(this, key);
    }

    originalRemoveItem.call(this, authKey(key));

    if (getAppPrefix() === "crm_") {
      originalRemoveItem.call(this, key);
    }
  };
}

export const getToken = () => localStorage.getItem("token");

export const getRole = () => localStorage.getItem("Role");

export const getUserId = () => localStorage.getItem("_id");

export const getEmail = () => localStorage.getItem("email");

export const getAuthSessionId = () => localStorage.getItem("authSessionId") || "";

export const parsePermissions = () => {
  const raw = localStorage.getItem("permissionArray") || "";
  return raw
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .map((p) => (p.startsWith("/") ? p : `/${p}`));
};

export const clearAuth = () => {
  MANAGED_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const setAuth = (user) => {
  clearAuth();

  const permissions = Array.isArray(user.permissions)
    ? user.permissions.join(",")
    : user.permissions || "";

  localStorage.setItem("email", user.details.email);
  localStorage.setItem("_id", user.details._id);
  localStorage.setItem("token", user.token);
  localStorage.setItem("loginTime", user.loginTime);
  localStorage.setItem("token_expire_time", user.token_expire_time);
  localStorage.setItem("permissionArray", permissions);
  localStorage.setItem("Role", user.details.role);
  localStorage.setItem("name", user.details.name);
  localStorage.setItem(
    "authSessionId",
    `${user.details._id}-${Date.now()}`
  );

  const hospitalId =
    user.details.hospital_id ??
    user.details.hospitalId ??
    user.details.hospital?.hospital_id ??
    user.details.hospital?.hospitalId ??
    user.details.hospital?.id ??
    "";
  if (hospitalId !== "" && hospitalId != null) {
    localStorage.setItem("hospital_id", String(hospitalId));
  }

  if (String(user.details?.role || "").toUpperCase() === "REFERRAL") {
    if (user.details?.referral_id) {
      localStorage.setItem("referral_id", String(user.details.referral_id));
    }
  } else {
    localStorage.removeItem("referral_id");
  }

  persistStaffAction(user);

  sessionStorage.setItem(getKnownUserIdKey(), user.details._id);
};

export const getHospitalId = () => localStorage.getItem("hospital_id") || "";

export const isReferralUser = () => {
  const role = (localStorage.getItem("Role") || "").toUpperCase();
  return role === "REFERRAL";
};

export const getReferralId = () => {
  if (isReferralUser()) {
    return localStorage.getItem("referral_id") || "";
  }
  if (localStorage.getItem("referral_id")) {
    localStorage.removeItem("referral_id");
  }
  return "";
};

export const isHospitalApp = () => getAppPrefix() === "hospital_";

export const isHospitalUser = () => {
  const role = (localStorage.getItem("Role") || "").toLowerCase();
  return isHospitalApp() || role === "hospital";
};

export const getLoginPath = () => {
  const publicUrl = process.env.PUBLIC_URL || "";
  if (publicUrl && publicUrl !== "/") {
    return publicUrl.endsWith("/") ? publicUrl : `${publicUrl}/`;
  }

  if (getAppPrefix() === "hospital_") {
    return "/hospital/";
  }

  return "/";
};

export const clearKnownUserId = () => {
  sessionStorage.removeItem(getKnownUserIdKey());
};

export const notifyAuthChanged = () => {
  window.dispatchEvent(new Event("auth-changed"));
};
